import type { DocumentData, DocumentSnapshot } from 'firebase/firestore'

import { callable } from '@/lib/firebase/callable'
import { createReasonGuard } from '@/lib/firebase/errors'
import type { Subscription } from '@/lib/firebase/subscribe'
import {
  type SetupScope,
  asOneOf,
  asRecords,
  asString,
  isRecord,
  subscribeLatestRun,
  toRunEntry,
  toSetupRun,
} from '@/repositories/setup-docs'
import {
  BELIEF_QUESTION_KEYS,
  CONTENT_STRATEGY_ERROR_CODES,
  CONTENT_STRATEGY_QUESTION_FAILED,
  CONTENT_STRATEGY_REFUSAL_REASONS,
  CONTENT_STRATEGY_STAGES,
  type BeliefQuestionKey,
  type BeliefRow,
  type ContentStrategyQuestionEntry,
  type ContentStrategyRun,
  type DraftedBelief,
} from '@/types/content-strategy-run'

function asQuestionKey(value: unknown): BeliefQuestionKey | undefined {
  return BELIEF_QUESTION_KEYS.find((key) => key === value)
}

function toEntry(entry: DocumentData): ContentStrategyQuestionEntry | undefined {
  const { errorCode, ...runEntry } = toRunEntry(entry)
  const questionKey = asQuestionKey(entry.questionKey)
  if (entry.kind !== 'question' || questionKey === undefined) return undefined
  return {
    ...runEntry,
    ...(errorCode !== undefined ? { errorCode: CONTENT_STRATEGY_QUESTION_FAILED } : {}),
    kind: 'question',
    questionKey,
  }
}

function toBelief(value: unknown): DraftedBelief | null {
  if (!isRecord(value)) return null
  const claim = asString(value.claim)
  if (claim === '') return null
  return { name: asString(value.name), claim, objection: asString(value.objection) }
}

function toBeliefRows(value: unknown): BeliefRow[] {
  return asRecords(value).flatMap((row) => {
    const questionKey = asQuestionKey(row.questionKey)
    return questionKey === undefined ? [] : [{ questionKey, belief: toBelief(row.belief) }]
  })
}

function toContentStrategyRun(
  snapshot: DocumentSnapshot<DocumentData>,
): ContentStrategyRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  const { errorCode, ...run } = toSetupRun(snapshot.id, data, CONTENT_STRATEGY_STAGES, toEntry)
  return {
    ...run,
    ...(errorCode !== undefined
      ? { errorCode: asOneOf(errorCode, CONTENT_STRATEGY_ERROR_CODES, 'content_strategy_failed') }
      : {}),
    keepEdited: data.keepEdited === true,
    startedByUserId: asString(data.startedByUserId),
    beliefs: toBeliefRows(data.beliefs),
  }
}

/** Live view of the brand's latest content strategy draft. */
export function subscribeLatestContentStrategyRun(
  scope: SetupScope,
): Subscription<ContentStrategyRun | null> {
  return subscribeLatestRun(scope, 'contentStrategyRuns', toContentStrategyRun)
}

export interface StartContentStrategyInput {
  keepEdited: boolean
}

export interface StartContentStrategyResult {
  runId: string
}

export const isContentStrategyRunInProgressReason = createReasonGuard([
  'content_strategy_run_in_progress',
] as const)

export const isContentStrategyRefusalReason = createReasonGuard(CONTENT_STRATEGY_REFUSAL_REASONS)

export const startContentStrategy = callable<StartContentStrategyInput, StartContentStrategyResult>(
  'startContentStrategy',
)

type EmptyInput = Record<string, never>

const confirmContentStrategyCallable = callable<EmptyInput, DocumentData>('confirmContentStrategy')

/** Approves the Belief Map and the Content Strategy together. */
export async function confirmContentStrategy(): Promise<void> {
  await confirmContentStrategyCallable({})
}
